import React from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin, Star, ArrowUpRight } from "lucide-react";
import { useTheme, currentBrandSlug } from "@/contexts/ThemeProvider";
import { useFavourites } from "@/contexts/FavouritesProvider";
import BrandCard from "@/components/brand/BrandCard";
import BrandLabel from "@/components/brand/BrandLabel";
import { cn } from "@/lib/utils";

export const PropertyCard = ({ item, layout = "vertical", showBrandBadge = false }) => {
  const { theme } = useTheme();
  const slug = currentBrandSlug(theme);
  const { has, toggle } = useFavourites();
  const isResidence = item.type === "residence" || !!item.bedrooms;
  const detailHref = isResidence
    ? `/brionj/r/${item.id}`
    : item.brand === "castlewood"
    ? `/castlewood/p/${item.id}`
    : `/vela/p/${item.id}`;

  const titleClass = cn(
    "font-display text-foreground",
    item.brand === "castlewood" || slug === "castlewood" ? "text-[22px] font-[700]" : "",
    item.brand === "vela" || slug === "vela" ? "text-[22px] font-[500]" : "",
    isResidence || slug === "brionj" ? "text-[26px] italic" : "",
    !(item.brand === "castlewood" || item.brand === "vela" || isResidence) && "text-[22px] font-[500]"
  );

  const wrapperBrandTheme =
    item.brand === "castlewood"
      ? "hover:border-[hsl(var(--brand-accent))]"
      : item.brand === "vela"
      ? "hover:border-[hsl(var(--primary))]/50"
      : isResidence
      ? "hover:border-[hsl(var(--brand-accent))]"
      : "hover:border-foreground/30";

  return (
    <BrandCard
      className={cn(
        "group overflow-hidden flex flex-col transition-colors duration-200",
        wrapperBrandTheme
      )}
      data-testid={`property-card-${item.id}`}
    >
      <Link to={detailHref} className="block relative">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={item.hero_image}
            alt={item.name}
            loading="lazy"
            className={cn(
              "w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]",
              (item.brand === "castlewood") && "castle-vignette"
            )}
          />
          {(item.brand === "castlewood") && (
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 55%, rgba(92, 39, 48, 0.20) 100%)" }} />
          )}
          {isResidence && (
            <div className="absolute inset-0 pointer-events-none border border-[hsl(var(--brand-accent))]/0 group-hover:border-[hsl(var(--brand-accent))]/40 transition-colors" />
          )}
          {showBrandBadge && (
            <div className="absolute top-3 left-3 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] bg-background/85 backdrop-blur-sm text-foreground border border-border">
              {item.brand}
            </div>
          )}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); toggle(item.id, item.name); }}
            className={cn(
              "absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-background/85 backdrop-blur-sm border border-border text-foreground hover:bg-background transition"
            )}
            aria-label={has(item.id) ? "Remove from favourites" : "Save to favourites"}
            data-testid={`fav-toggle-${item.id}`}
          >
            <Heart size={14} className={cn(has(item.id) && "fill-current text-[hsl(var(--primary))]")} />
          </button>
        </div>
      </Link>
      <div className={cn("p-5 md:p-6 flex flex-col gap-3 flex-1", isResidence && "p-6 md:p-7")}>
        <BrandLabel className="text-[11px]">{isResidence ? "BRIONJ" : item.brand?.toUpperCase()} · {item.location}</BrandLabel>
        <Link to={detailHref} className={titleClass}>{item.name}</Link>
        {item.tagline && (
          <p className="text-[14px] text-muted-foreground line-clamp-2 font-body">{item.tagline}</p>
        )}
        <div className="mt-auto flex items-center justify-between pt-3">
          <div className="flex items-center gap-3 text-[12px] text-muted-foreground">
            {item.rating && (
              <span className="inline-flex items-center gap-1"><Star size={12} className="fill-current" /> {item.rating.toFixed(1)}</span>
            )}
            {item.bedrooms && (<span>{item.bedrooms} bedrooms</span>)}
            {item.sleeps && (<span>Sleeps {item.sleeps}</span>)}
          </div>
          <div className="text-[13px]">
            {item.price_from && (
              <span className="font-ui"><span className="text-muted-foreground">from</span> <span className="font-medium text-foreground">€{Math.round(item.price_from)}</span></span>
            )}
            {!item.price_from && isResidence && (
              <span className="font-ui smallcaps text-[10px] text-[hsl(var(--brand-accent))]">By enquiry</span>
            )}
          </div>
        </div>
        <Link to={detailHref} className="inline-flex items-center gap-1 mt-2 text-[12px] underline underline-offset-[6px] decoration-[hsl(var(--brand-accent))] hover:decoration-foreground transition-colors" data-testid={`view-${item.id}`}>
          {isResidence ? "View residence" : "Discover"} <ArrowUpRight size={12} />
        </Link>
      </div>
    </BrandCard>
  );
};

export default PropertyCard;
